import { useCallback, useMemo } from 'react'
import type { Dayjs } from 'dayjs'
import dayjs from 'helpers/datetime'
import { useRouterContext } from 'components/RouterContext'

const MONTH_QUERY_FORMAT = 'YYYY-MM'

// Invoices for month M are funded on a run on day 10–15 of month M-1, so once
// we pass the 10th the team is working on the *next* month — default to it.
export const getDefaultBillingMonth = (): Dayjs => {
  const now = dayjs()
  return now.date() >= 10
    ? now.add(1, 'month').startOf('month')
    : now.startOf('month')
}

/**
 * Billing month shared across Invoice Management modes via the `month=YYYY-MM`
 * URL query param, so switching between Generation and Delivery preserves the
 * selection. Falls back to {@link getDefaultBillingMonth} when absent/invalid.
 */
export const useSelectedBillingMonth = () => {
  const { router, location } = useRouterContext()

  const monthParam = location?.query?.month as string | undefined
  const selectedMonth = useMemo<Dayjs>(() => {
    const parsed = monthParam ? dayjs(`${monthParam}-01`) : null
    return parsed?.isValid()
      ? parsed.startOf('month')
      : getDefaultBillingMonth()
  }, [monthParam])

  const setSelectedMonth = useCallback(
    (next: Dayjs) => {
      router?.push({
        pathname: location?.pathname,
        query: {
          ...(location?.query ?? {}),
          month: next.startOf('month').format(MONTH_QUERY_FORMAT),
        },
      })
    },
    [router, location?.pathname, location?.query]
  )

  return {
    selectedMonth,
    setSelectedMonth,
    billingMonth: selectedMonth.month() + 1,
    billingYear: selectedMonth.year(),
    billingPeriodStart: selectedMonth.startOf('month').format('YYYY-MM-DD'),
  }
}
